import { useEffect, useState } from "react";
import { api } from "@/lib/api";

const W = 800;
const H = 280;
const PAD = 40;

const truncate = (s, n = 26) => (s.length > n ? `${s.slice(0, n).trimEnd()}…` : s);

export function RouteMap({ pickup, dropoff, stops = [] }) {
  const [geo, setGeo] = useState(null);
  const stopKey = stops.filter(Boolean).join("|");

  useEffect(() => {
    if (!pickup.trim() || !dropoff.trim()) {
      setGeo(null);
      return;
    }
    const params = new URLSearchParams({ pickup, dropoff });
    stops.filter(Boolean).forEach((s) => params.append("stops", s));
    let live = true;
    api
      .get(`/geo/route?${params.toString()}`)
      .then((r) => live && setGeo(r.data))
      .catch(() => live && setGeo(null));
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickup, dropoff, stopKey]);

  if (!geo || !geo.path?.length) return null;

  const lats = geo.path.map((p) => p[0]);
  const lngs = geo.path.map((p) => p[1]);
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs);
  const maxLng = Math.max(...lngs);
  const spanLat = Math.max(maxLat - minLat, 1e-4);
  const spanLng = Math.max(maxLng - minLng, 1e-4);
  // fit preserving aspect ratio of the geography
  const geoAspect = spanLng / spanLat;
  const boxAspect = (W - 2 * PAD) / (H - 2 * PAD);
  let wEff = W - 2 * PAD;
  let hEff = H - 2 * PAD;
  if (geoAspect > boxAspect) hEff = wEff / geoAspect;
  else wEff = hEff * geoAspect;
  const offX = (W - wEff) / 2;
  const offY = (H - hEff) / 2;
  const project = ([lat, lng]) => [
    offX + ((lng - minLng) / spanLng) * wEff,
    H - offY - ((lat - minLat) / spanLat) * hEff,
  ];

  const polyline = geo.path
    .map((p) => project(p).map((n) => n.toFixed(1)).join(","))
    .join(" ");
  const markers = (geo.points || []).map((p, i) => ({
    ...p,
    i,
    xy: project([p.lat, p.lng]),
    kind: i === 0 ? "start" : i === geo.points.length - 1 ? "end" : "stop",
  }));

  const gridLines = [];
  for (let x = 0; x <= W; x += 80) gridLines.push(["v", x]);
  for (let y = 0; y <= H; y += 80) gridLines.push(["h", y]);

  return (
    <div className="border border-[#2E3340] overflow-hidden" data-testid="route-map">
      <div className="relative bg-[#0B0C0F]">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full block" role="img" aria-label="Route map">
          <defs>
            <linearGradient id="routeGold" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#8a6b1f" />
              <stop offset="50%" stopColor="#C5A059" />
              <stop offset="100%" stopColor="#e8cd82" />
            </linearGradient>
            <filter id="routeGlow" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="4" />
            </filter>
          </defs>
          {gridLines.map(([dir, v], i) =>
            dir === "v" ? (
              <line key={i} x1={v} y1={0} x2={v} y2={H} stroke="#C5A059" strokeOpacity="0.05" strokeWidth="1" />
            ) : (
              <line key={i} x1={0} y1={v} x2={W} y2={v} stroke="#C5A059" strokeOpacity="0.05" strokeWidth="1" />
            )
          )}
          <polyline points={polyline} fill="none" stroke="#C5A059" strokeOpacity="0.35" strokeWidth="7" strokeLinecap="round" strokeLinejoin="round" filter="url(#routeGlow)" />
          <polyline points={polyline} fill="none" stroke="url(#routeGold)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          {markers.map((m) => (
            <g key={`${m.i}-${m.lat}-${m.lng}`} data-testid={`route-marker-${m.i}`}>
              <circle cx={m.xy[0]} cy={m.xy[1]} r="10" fill="none" stroke={m.kind === "start" ? "#34d399" : m.kind === "end" ? "#C5A059" : "#a1a1aa"} strokeOpacity="0.35" strokeWidth="1.5" />
              <circle cx={m.xy[0]} cy={m.xy[1]} r="4.5" fill={m.kind === "start" ? "#34d399" : m.kind === "end" ? "#C5A059" : "#a1a1aa"} stroke="#0B0C0F" strokeWidth="1.5" />
            </g>
          ))}
          {(() => {
            const placed = [];
            return markers.map((m) => {
              const above = m.xy[1] > 96;
              let lx = Math.min(Math.max(m.xy[0], 92), W - 92);
              let ly = above ? m.xy[1] - 30 : m.xy[1] + 34;
              let tries = 0;
              while (placed.some((p) => Math.abs(p.x - lx) < 170 && Math.abs(p.y - ly) < 22) && tries < 4) {
                ly = above ? ly - 26 : ly + 26;
                lx = Math.min(Math.max(lx + (tries % 2 === 0 ? 70 : -70), 92), W - 92);
                tries++;
              }
              placed.push({ x: lx, y: ly });
              return (
                <g key={`label-${m.i}`}>
                  <line x1={m.xy[0]} y1={m.xy[1]} x2={lx} y2={above ? ly + 12 : ly - 12} stroke="#2E3340" strokeWidth="1" strokeDasharray="2 3" />
                  <text
                    x={lx}
                    y={ly}
                    textAnchor="middle"
                    fill={m.kind === "start" ? "#6ee7b7" : m.kind === "end" ? "#e8cd82" : "#d4d4d8"}
                    fontSize="12.5"
                    fontFamily="ui-monospace, monospace"
                    letterSpacing="0.06em"
                  >
                    {truncate(m.label)}
                  </text>
                </g>
              );
            });
          })()}
        </svg>
      </div>
      <p className="px-4 py-2.5 bg-[#1E2128] border-t border-[#2E3340] font-mono-lux text-[10px] font-semibold uppercase tracking-[0.25em] text-zinc-400">
        Your route · {geo.miles} mi · ~{geo.duration_minutes} min
      </p>
    </div>
  );
}
